import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useApi, post, put, Panel, PageHead, QuickForm, DataTable, StatusBadge, Tabs, inr } from "@/crm/ui";

export default function Pipeline() {
  const nav = useNavigate();
  const [tab, setTab] = useState("Leads");
  const [form, setForm] = useState({});
  const [statusForm, setStatusForm] = useState({});
  const leads = useApi("/crm/leads");
  const apps = useApi("/crm/applications");
  const banks = useApi("/crm/banks");
  const products = useApi("/crm/products");
  const employees = useApi("/crm/employees");
  const partners = useApi("/crm/partners");
  const meta = useApi("/crm/meta");
  const analyses = useApi("/api/analyses".replace("/api", ""));
  const bankId = form.bank_id;
  const locations = useApi(bankId ? `/crm/banks/${bankId}/locations` : null, [bankId]);
  const managers = useApi(bankId ? `/crm/banks/${bankId}/managers` : null, [bankId]);
  const bankProfile = useApi(bankId ? `/crm/banks/${bankId}` : null, [bankId]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const opt = (rows, idKey, labelKey) => (rows || []).map((r) => ({ value: r[idKey], label: `${r[labelKey]} (${r[idKey]})` }));

  return (
    <div className="space-y-6" data-testid="pipeline-page">
      <PageHead eyebrow="CRM" title="Leads & Applications"
                subtitle="One lead can be logged into multiple banks. Bank managers are restricted to the selected bank and location." />
      <Tabs tabs={["Leads", "Applications / Login", "Status & Disbursement"]} active={tab}
            onChange={(t) => { setTab(t); setForm({}); }} testid="pipeline-tabs" />

      {tab === "Leads" && (
        <>
          <Panel title="Create Lead" testid="create-lead-panel">
            <QuickForm testid="lead-form" values={form} onChange={set} submitLabel="Create Lead"
                       fields={[{ key: "customer_name", label: "Customer Name" },
                         { key: "mobile", label: "Mobile" }, { key: "email", label: "Email" },
                         { key: "pan", label: "PAN" }, { key: "gstin", label: "GSTIN" },
                         { key: "loan_amount", label: "Loan Amount", type: "number" },
                         { key: "city", label: "City" }, { key: "state", label: "State" },
                         { key: "source", label: "Source", options: ["Direct", "Partner", "Reference", "Digital", "Walk-in", "Other"] },
                         { key: "employee_id", label: "Assigned Employee", options: opt(employees.data, "employee_id", "employee_name") },
                         { key: "partner_id", label: "Assigned Partner", options: opt(partners.data, "partner_id", "partner_name") },
                         { key: "bank_id", label: "Preferred Bank", options: opt(banks.data, "bank_id", "bank_name") },
                         { key: "product_id", label: "Preferred Product", options: opt(products.data, "product_id", "product_name") },
                         { key: "next_followup", label: "Next Follow-up", type: "date" },
                         { key: "remarks", label: "Remarks" }]}
                       onSubmit={async () => {
                         const r = await post("/crm/leads", form, "Lead created");
                         if (r) { setForm({}); leads.reload(); }
                       }} />
          </Panel>
          <Panel title={`Leads (${(leads.data || []).length})`} testid="leads-panel"
                 aside={<span className="text-xs text-muted-foreground">Open a lead for assignment, statement analysis and multi-bank applications</span>}>
            <DataTable testid="leads-table" rows={leads.data} onRowClick={(r) => nav(`/crm/leads/${r.lead_id}`)}
                       columns={[{ key: "lead_id", label: "Lead ID" }, { key: "customer_name", label: "Customer" },
                         { key: "mobile", label: "Mobile" }, { key: "pan", label: "PAN" },
                         { key: "loan_amount", label: "Requirement", right: true, render: (r) => inr(r.loan_amount) },
                         { key: "source", label: "Source" }, { key: "employee_id", label: "Employee" },
                         { key: "partner_id", label: "Partner" },
                         { key: "applications", label: "Bank Logins", right: true },
                         { key: "stage", label: "Stage", render: (r) => <StatusBadge value={r.stage} /> },
                         { key: "open", label: "", render: (r) => (
                           <Button size="sm" variant="outline" className="bg-white text-[10px]"
                                   data-testid={`open-lead-${r.lead_id}`}
                                   onClick={(e) => { e.stopPropagation(); nav(`/crm/leads/${r.lead_id}`); }}>
                             Open</Button>) },
                         { key: "link", label: "Statement", render: (r) => (
                           <select className="h-8 rounded border border-input bg-white text-xs"
                                   data-testid={`link-statement-${r.lead_id}`}
                                   onClick={(e) => e.stopPropagation()}
                                   onChange={async (e) => {
                                     if (!e.target.value) return;
                                     await post(`/crm/leads/${r.lead_id}/link-statement`, { job_id: e.target.value },
                                       "Statement linked to lead");
                                     leads.reload();
                                   }}>
                             <option value="">Link analysis…</option>
                             {(analyses.data || []).filter((a) => a.status === "ready").map((a) => (
                               <option key={a.job_id} value={a.job_id}>{(a.file_names || [])[0] || a.job_id}</option>
                             ))}
                           </select>) }]} />
          </Panel>
        </>
      )}

      {tab === "Applications / Login" && (
        <>
          <Panel title="Create Application / Bank Login" testid="create-application-panel">
            <QuickForm testid="application-form" values={form} onChange={set} submitLabel="Create Login"
                       fields={[{ key: "lead_id", label: "Lead", options: opt(leads.data, "lead_id", "customer_name") },
                         { key: "bank_id", label: "Bank", options: opt(banks.data, "bank_id", "bank_name") },
                         { key: "location_id", label: "Bank Location", options: opt(locations.data, "location_id", "location_name") },
                         { key: "product_id", label: "Bank Product (mapped only)", options: (bankProfile.data?.products || []).map((p) => ({ value: p.product_id, label: p.product_name })) },
                         { key: "manager_id", label: "Bank Manager", options: opt(managers.data, "manager_id", "manager_name") },
                         { key: "application_number", label: "Bank Application Number" },
                         { key: "login_amount", label: "Login Amount", type: "number" },
                         { key: "login_date", label: "Login Date", type: "date" },
                         { key: "remarks", label: "Remarks" }]}
                       onSubmit={async () => {
                         const r = await post("/crm/applications", form, "Application created");
                         if (r) { setForm({}); apps.reload(); leads.reload(); }
                       }} />
          </Panel>
          <Panel title={`Applications (${(apps.data || []).length})`} testid="applications-panel">
            <DataTable testid="applications-table" rows={apps.data} onRowClick={(r) => nav(`/crm/leads/${r.lead_id}`)}
                       columns={[{ key: "application_id", label: "Application" }, { key: "lead_id", label: "Lead" },
                         { key: "customer_name", label: "Customer" }, { key: "bank_name", label: "Bank" },
                         { key: "location_name", label: "Location" }, { key: "product_name", label: "Product" },
                         { key: "login_amount", label: "Login", right: true, render: (r) => inr(r.login_amount) },
                         { key: "approved_amount", label: "Approved", right: true, render: (r) => inr(r.approved_amount) },
                         { key: "disbursed_amount", label: "Disbursed", right: true, render: (r) => inr(r.disbursed_amount) },
                         { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> }]} />
          </Panel>
        </>
      )}

      {tab === "Status & Disbursement" && (
        <Panel title="Update application status" testid="status-panel"
               aside={<span className="text-xs text-muted-foreground">Disbursement automatically calculates commission &amp; payout</span>}>
          <QuickForm testid="status-form" values={statusForm}
                     onChange={(k, v) => setStatusForm((f) => ({ ...f, [k]: v }))}
                     submitLabel="Apply status change"
                     fields={[{ key: "application_id", label: "Application", options: opt(apps.data, "application_id", "customer_name") },
                       { key: "status", label: "New Status", options: (meta.data || {}).application_statuses || [] },
                       { key: "approved_amount", label: "Approved Amount", type: "number" },
                       { key: "disbursed_amount", label: "Disbursed Amount", type: "number" },
                       { key: "disbursement_date", label: "Disbursement Date", type: "date" },
                       { key: "hold_reason", label: "Hold Reason" },
                       { key: "rejection_reason", label: "Rejection Reason", options: (meta.data || {}).rejection_reasons || [] },
                       { key: "remarks", label: "Remarks" }]}
                     onSubmit={async () => {
                       const { application_id, ...rest } = statusForm;
                       if (!application_id) return;
                       const r = await put(`/crm/applications/${application_id}/status`, rest, "Status updated");
                       if (r) {
                         setStatusForm({}); apps.reload();
                         if (r.payout) window.alert(`Payout ${r.payout.payout_id} calculated — net payable ${r.payout.net_payable}`);
                       }
                     }} />
          <div className="mt-6">
            <DataTable testid="status-history-table" rows={apps.data}
                       columns={[{ key: "application_id", label: "Application" }, { key: "bank_name", label: "Bank" },
                         { key: "status", label: "Current", render: (r) => <StatusBadge value={r.status} /> },
                         { key: "hold_reason", label: "Hold Reason" },
                         { key: "rejection_reason", label: "Rejection Reason" },
                         { key: "history", label: "Status changes", right: true,
                           render: (r) => (r.status_history || []).length }]} />
          </div>
        </Panel>
      )}
    </div>
  );
}
